/**
 * Dashboard user accounts (User Management), their history and notes.
 *
 * The five demo personas are real rows here with the SAME id and email the
 * login returns (personas.ts), so impersonation and "edit yourself" behave.
 * Everyone else is an invented staff member from world.ts with one role.
 */
import { DATA_START, addDays, iso, now } from '../../clock';
import { PERSONAS, Persona } from '../../personas';
import { rng } from '../../rng';
import { getState } from '../../state';
import { LEADERSHIP, ORG, STAFF, Staff, fakeUlid } from '../../world';
import { daysAgoAt } from './time';

export const ROLE_LABELS: Record<string, string> = {
    ROLE_USER: 'Staff (own profile only)',
    ROLE_BOARD: 'Board',
    ROLE_CSC: 'Client Services',
    ROLE_HR: 'HR',
    ROLE_MANAGER: 'Management',
    ROLE_ADMIN: 'Admin',
};

/** Order of UserRoles::choices(), used by the user and filter forms. */
export const ROLE_ORDER = ['ROLE_USER', 'ROLE_BOARD', 'ROLE_CSC', 'ROLE_HR', 'ROLE_MANAGER', 'ROLE_ADMIN'];

export interface AdminUser {
    id: string;
    name: string;
    email: string;
    username: string | null;
    roles: string[];
    locked: boolean;
    archived: boolean;
    phone: string | null;
    jobTitle: string;
    created: Date;
    createdById: string | null;
    notificationMethods: string[];
    notificationStreams: string[];
    personaKey: string | null;
    staffId: number | null;
    /** Seeded history before the demo session (newest last). */
    seedHistory: HistoryEntry[];
}

export interface HistoryEntry {
    id: string;
    version: number;
    action: 'create' | 'update' | 'remove';
    username: string | null;
    loggedAt: string;
    data: Record<string, unknown>;
}

export interface NoteJson {
    id: string;
    message: string;
    file: null;
    files: unknown[];
    user: Record<string, unknown>;
    created: string;
    updated: string;
}

const LEADER_NAMES = new Set(Object.values(LEADERSHIP).map(l => l.name));

const STAFF_ACCOUNTS: [string, string][] = [
    ['Team Leader – Disability', 'ROLE_MANAGER'],
    ['Team Leader – Aged Care', 'ROLE_MANAGER'],
    ['DFV Practice Lead', 'ROLE_MANAGER'],
    ['Youth Program Coordinator', 'ROLE_MANAGER'],
    ['Ngangkari Program Coordinator', 'ROLE_MANAGER'],
    ['Program Coordinator – Nutrition', 'ROLE_MANAGER'],
    ['Support Coordinator', 'ROLE_CSC'],
    ['Home Care Coordinator', 'ROLE_CSC'],
    ['Quality & Risk Advisor', 'ROLE_CSC'],
    ['HR Advisor', 'ROLE_HR'],
    ['Payroll Officer', 'ROLE_HR'],
    ['IT Support Officer', 'ROLE_ADMIN'],
    ['Executive Assistant', 'ROLE_USER'],
];

const ceoId = fakeUlid('persona:ceo');
const ceoEmail = PERSONAS[0].email;

function phoneFor(seed: string): string {
    const r = rng(`phone:${seed}`);
    return `04${r.int(0, 9)}${r.int(0, 9)} ${r.int(100, 999)} ${r.int(100, 999)}`;
}

function usernameFor(name: string): string {
    const [first, ...rest] = name.toLowerCase().replace(/[^a-z ]/g, '').split(' ');
    return `${first.charAt(0)}${rest.join('')}`;
}

function at(d: Date, hour: number, minute: number): Date {
    const x = new Date(d);
    x.setHours(hour, minute, 0, 0);
    return x;
}

function buildUsers(): AdminUser[] {
    const r = rng('admin:users');
    const out: AdminUser[] = [];
    let seq = 0;
    const histId = (uid: string) => fakeUlid(`log:${uid}:${seq++}`);

    const add = (u: Omit<AdminUser, 'seedHistory' | 'notificationMethods' | 'notificationStreams'>, extraHistory: { daysAfter: number; data: Record<string, unknown> }[] = []) => {
        const createdBy = u.createdById === ceoId ? ceoEmail : null;
        const history: HistoryEntry[] = [
            {
                id: histId(u.id),
                version: 1,
                action: 'create',
                username: createdBy,
                loggedAt: iso(u.created),
                data: { email: u.email, name: u.name, roles: ['ROLE_USER'], locked: false },
            },
            {
                id: histId(u.id),
                version: 2,
                action: 'update',
                username: createdBy,
                loggedAt: iso(new Date(u.created.getTime() + 3 * 60000)),
                data: { roles: u.roles },
            },
        ];
        for (const h of extraHistory) {
            const when = addDays(u.created, h.daysAfter);
            if (when < now()) {
                history.push({ id: histId(u.id), version: history.length + 1, action: 'update', username: h.data['locked'] !== undefined || h.data['archived'] !== undefined ? ceoEmail : u.email, loggedAt: iso(when), data: h.data });
            }
        }
        out.push({
            ...u,
            notificationMethods: u.roles.includes('ROLE_BOARD') ? ['EMAIL'] : ['EMAIL', 'PUSH'],
            notificationStreams: u.roles.includes('ROLE_BOARD') ? ['board'] : ['sync', 'support', 'digest'],
            seedHistory: history,
        });
    };

    // Personas: provisioned first, when the dashboard went live.
    const live = addDays(DATA_START, 3);
    PERSONAS.forEach((p, i) => {
        const created = at(addDays(live, i === 0 ? 0 : r.int(1, 30)), 9 + i, r.int(0, 59));
        add(
            {
                id: fakeUlid(`persona:${p.key}`),
                name: p.name,
                email: p.email,
                username: p.email.split('@')[0],
                roles: [...p.roles],
                locked: false,
                archived: false,
                phone: phoneFor(p.key),
                jobTitle: p.title,
                created,
                createdById: i === 0 ? null : ceoId,
                personaKey: p.key,
                staffId: null,
            },
            i % 2 === 0 ? [{ daysAfter: r.int(20, 120), data: { phone: phoneFor(p.key) } }] : [],
        );
    });

    // The CFO: a second admin.
    add({
        id: fakeUlid('user:cfo'),
        name: LEADERSHIP.cfo.name,
        email: `finance@${ORG.domain}`,
        username: 'finance',
        roles: ['ROLE_ADMIN'],
        locked: false,
        archived: false,
        phone: phoneFor('cfo'),
        jobTitle: LEADERSHIP.cfo.title,
        created: at(addDays(live, 2), 11, 20),
        createdById: ceoId,
        personaKey: null,
        staffId: null,
    });

    const staffUser = (s: Staff, role: string, created: Date, extra: Partial<AdminUser> = {}) => ({
        id: fakeUlid(`user:${s.id}`),
        name: s.name,
        email: s.email,
        username: usernameFor(s.name),
        roles: [role],
        locked: false,
        archived: false,
        phone: r.bool(0.6) ? phoneFor(s.email) : null,
        jobTitle: s.position,
        created,
        createdById: ceoId,
        personaKey: null,
        staffId: s.id,
        ...extra,
    });

    const used = new Set<number>();
    const daysLive = Math.max(30, Math.round((now().getTime() - live.getTime()) / 86400000) - 14);
    for (const [position, role] of STAFF_ACCOUNTS) {
        const s = STAFF.find(x => !x.endDate && x.position === position && !used.has(x.id) && !LEADER_NAMES.has(x.name));
        if (!s) {
            continue;
        }
        used.add(s.id);
        const created = at(addDays(live, r.int(20, daysLive)), r.int(8, 16), r.int(0, 59));
        add(staffUser(s, role, created), r.bool(0.3) ? [{ daysAfter: r.int(5, 40), data: { name: s.name } }] : []);
    }

    // A team leader who has since left: account locked, kept for the audit trail.
    const leaver = STAFF.find(x => !!x.endDate && /Lead|Coordinator/.test(x.position) && !LEADER_NAMES.has(x.name) && new Date(x.endDate) > addDays(live, 40));
    if (leaver) {
        const created = at(addDays(live, r.int(10, 30)), 10, 5);
        const lockDays = Math.max(1, Math.round((new Date(leaver.endDate as string).getTime() - created.getTime()) / 86400000) + 1);
        add(staffUser(leaver, 'ROLE_MANAGER', created, { locked: true }), [{ daysAfter: lockDays, data: { locked: true } }]);
    }
    // An earlier leaver whose account was locked and then archived.
    const archived = STAFF.find(x => !!x.endDate && x.id !== leaver?.id && x.program !== 'Corporate Services' && !LEADER_NAMES.has(x.name) && new Date(x.endDate) > addDays(live, 20));
    if (archived) {
        const created = at(addDays(live, r.int(5, 15)), 14, 40);
        const lockDays = Math.max(1, Math.round((new Date(archived.endDate as string).getTime() - created.getTime()) / 86400000) + 1);
        add(staffUser(archived, 'ROLE_CSC', created, { locked: true, archived: true }), [
            { daysAfter: lockDays, data: { locked: true } },
            { daysAfter: lockDays + 7, data: { archived: true } },
        ]);
    }

    // API Platform default order: id DESC, i.e. newest account first.
    return out.sort((a, b) => b.created.getTime() - a.created.getTime());
}

/** The live, mutable user table for this tab. */
export function adminUsers(): AdminUser[] {
    return getState('admin:users', buildUsers);
}

export function findUser(id: string): AdminUser | undefined {
    return adminUsers().find(u => u.id === id);
}

export function userByEmail(email: string | null | undefined): AdminUser | undefined {
    const e = (email || '').trim().toLowerCase();
    return adminUsers().find(u => u.email.toLowerCase() === e);
}

export function userForPersona(p: Persona): AdminUser {
    return userByEmail(p.email) || adminUsers()[0];
}

export const displayString = (u: AdminUser): string => `${u.name}${u.locked ? '(Disabled)' : ''}`;

/** Small nested user object (createdBy, note authors). */
export function userRef(u: AdminUser | undefined | null): Record<string, unknown> | null {
    if (!u) {
        return null;
    }
    return { id: u.id, string: displayString(u), name: u.name, email: u.email, photo: null, archived: u.archived };
}

function sessionHistory(id: string): HistoryEntry[] {
    const all = getState<Record<string, HistoryEntry[]>>('admin:userHistory', () => ({}));
    return (all[id] = all[id] || []);
}

/** Full history for a user, newest first. */
export function userHistory(id: string): HistoryEntry[] {
    const u = findUser(id);
    if (!u) {
        return [];
    }
    return [...u.seedHistory, ...sessionHistory(id)].sort((a, b) => b.version - a.version);
}

export function recordUserChange(u: AdminUser, actor: Persona, data: Record<string, unknown>): void {
    if (!Object.keys(data).length) {
        return;
    }
    const version = u.seedHistory.length + sessionHistory(u.id).length + 1;
    sessionHistory(u.id).push({ id: fakeUlid(`log:${u.id}:session:${version}`), version, action: 'update', username: actor.email, loggedAt: iso(now()), data });
}

/** Serialized User. `item` adds the item-only groups (created/updated/createdBy/updatedBy). */
export function userJson(u: AdminUser, item = false): Record<string, unknown> {
    const out: Record<string, unknown> = {
        id: u.id,
        string: displayString(u),
        archived: u.archived,
        name: u.name,
        photo: null,
        email: u.email,
        username: u.username,
        roles: [...u.roles],
        roleLabels: u.roles.map(r => ROLE_LABELS[r] || r),
        locked: u.locked,
        phone: u.phone,
        jobTitle: u.jobTitle,
    };
    if (item) {
        const history = userHistory(u.id);
        const latest = history[0];
        const updater = latest?.username ? userByEmail(latest.username) : null;
        out['created'] = iso(u.created);
        out['updated'] = latest ? latest.loggedAt : iso(u.created);
        out['createdBy'] = userRef(u.createdById ? findUser(u.createdById) : null);
        out['updatedBy'] = userRef(updater);
    }
    return out;
}

/**
 * The /api/v1/user/current body for an email (login identity or X-Switch-User).
 * Echoes the email EXACTLY, even for an unknown address, so the app's
 * user.watch loop settles (see gaps.md GAP 4).
 */
export function currentUserJson(email: string): Record<string, unknown> {
    const u = userByEmail(email);
    if (u) {
        const json = userJson(u);
        json['email'] = email;
        return json;
    }
    return {
        id: fakeUlid(`stub:${email}`),
        string: email,
        archived: false,
        name: email,
        photo: null,
        email,
        username: null,
        roles: ['ROLE_USER'],
        roleLabels: [ROLE_LABELS['ROLE_USER']],
        locked: false,
        phone: null,
    };
}

/** Users list query: archived toggle, simplesearch, filter JSON and the three sortable columns. */
export function queryUsers(query: URLSearchParams): AdminUser[] {
    const showArchived = ['true', '1'].includes(query.get('archived') || '');
    let rows = adminUsers().filter(u => u.archived === showArchived);

    const search = (query.get('simplesearch') || '').trim().toLowerCase();
    if (search) {
        rows = rows.filter(u => u.name.toLowerCase().includes(search) || u.email.toLowerCase().includes(search) || u.jobTitle.toLowerCase().includes(search));
    }

    let filter: Record<string, unknown> = {};
    try {
        filter = JSON.parse(query.get('filter') || '{}') || {};
    } catch {
        filter = {};
    }
    const role = filter['role'];
    if (typeof role === 'string' && role) {
        rows = rows.filter(u => u.roles.includes(role));
    }
    const locked = filter['locked'];
    if (locked !== undefined && locked !== null && locked !== '') {
        const want = locked === true || locked === '1' || locked === 1;
        rows = rows.filter(u => u.locked === want);
    }

    const order = (key: string) => query.get(`order[${key}]`);
    const dir = (v: string | null) => (v === 'desc' ? -1 : 1);
    const last = (n: string) => n.split(' ').slice(-1)[0];
    if (order('firstName') || order('name')) {
        const d = dir(order('firstName') || order('name'));
        rows = [...rows].sort((a, b) => d * a.name.localeCompare(b.name));
    } else if (order('lastName')) {
        const d = dir(order('lastName'));
        rows = [...rows].sort((a, b) => d * last(a.name).localeCompare(last(b.name)));
    } else if (order('email')) {
        const d = dir(order('email'));
        rows = [...rows].sort((a, b) => d * a.email.localeCompare(b.email));
    } else if (order('locked')) {
        const d = dir(order('locked'));
        rows = [...rows].sort((a, b) => d * (Number(a.locked) - Number(b.locked)));
    }
    return rows;
}

// ---------------------------------------------------------------- notes

const USER_NOTES: Record<string, string[]> = {
    ROLE_ADMIN: ['Admin access confirmed with the Board — needed for budget uploads and user management.'],
    ROLE_MANAGER: [
        'Management access so the program dashboards can be reviewed at the monthly leadership meeting.',
        'Asked for a walkthrough of the finance page — booked in for next Tuesday.',
    ],
    ROLE_CSC: ['Client Services access for incident and medication follow-up across the communities.'],
    ROLE_HR: ['HR access to review workplace compliance and training ahead of the quarterly report.'],
    ROLE_BOARD: ['Board read-only access. Board pack link sent before each meeting.'],
    ROLE_USER: ['Own-profile access only, for checking personal compliance items.'],
};

const OBJECT_NOTES = [
    'Followed up by phone; supporting documents are on the way this week.',
    'Discussed at the Thursday intake meeting — no further action needed for now.',
    'Family asked for a call back after the next community visit.',
    'Checked with the regional coordinator; details confirmed and file updated.',
];

function sessionNotes(objectId: string): NoteJson[] {
    const all = getState<Record<string, NoteJson[]>>('admin:notes', () => ({}));
    return (all[objectId] = all[objectId] || []);
}

/** Seeded notes (stable per object id), followed by any added in this tab. Newest first. */
export function notesFor(objectId: string): NoteJson[] {
    const r = rng(`notes:${objectId}`);
    const u = findUser(objectId);
    const seeded: NoteJson[] = [];
    if (u) {
        const authorUser = u.personaKey === 'ceo' ? findUser(fakeUlid('persona:hr')) : findUser(ceoId);
        const pool = USER_NOTES[u.roles[0]] || [];
        pool.slice(0, r.int(0, pool.length)).forEach((message, i) => {
            const when = addDays(u.created, i * r.int(7, 30));
            if (when < now()) {
                seeded.push(noteJson(fakeUlid(`note:${objectId}:${i}`), message, authorUser, when));
            }
        });
    } else {
        const author = findUser(fakeUlid('persona:care'));
        const count = r.int(0, 2);
        for (let i = 0; i < count; i++) {
            seeded.push(noteJson(fakeUlid(`note:${objectId}:${i}`), r.pick(OBJECT_NOTES), author, daysAgoAt(r.int(1, 25), r.int(9, 16), r.int(0, 59))));
        }
    }
    const edits = noteEdits();
    const all = [...seeded, ...sessionNotes(objectId)].map(n => (edits[n.id] ? { ...n, ...edits[n.id] } : n));
    return all.sort((a, b) => b.created.localeCompare(a.created));
}

function noteEdits(): Record<string, { message: string; updated: string }> {
    return getState('admin:noteEdits', () => ({}));
}

function noteJson(id: string, message: string, author: AdminUser | undefined, when: Date, updated?: Date): NoteJson {
    return { id, message, file: null, files: [], user: userRef(author) || {}, created: iso(when), updated: iso(updated || when) };
}

export function findNote(noteId: string): { objectId: string; note: NoteJson } | null {
    const all = getState<Record<string, NoteJson[]>>('admin:notes', () => ({}));
    for (const [objectId, list] of Object.entries(all)) {
        const note = list.find(n => n.id === noteId);
        if (note) {
            return { objectId, note };
        }
    }
    return null;
}

/** Create (or edit, when `noteId` names one added this session) a note. */
export function saveNote(persona: Persona, body: Record<string, any>, noteId?: string): NoteJson {
    const message = String(body?.['message'] ?? '').trim();
    if (noteId) {
        const found = findNote(noteId);
        if (found) {
            found.note.message = message || found.note.message;
            found.note.updated = iso(now());
            return found.note;
        }
        // A seeded note: remember the edit so the list shows it.
        const edit = { message, updated: iso(now()) };
        noteEdits()[noteId] = edit;
        return { id: noteId, message, file: null, files: [], user: userRef(userForPersona(persona)) || {}, created: edit.updated, updated: edit.updated };
    }
    const objectId = String(body?.['objectId'] ?? 'unknown');
    const list = sessionNotes(objectId);
    const note = noteJson(fakeUlid(`note:${objectId}:session:${list.length}:${message.length}`), message, userForPersona(persona), now());
    list.push(note);
    return note;
}
